import { Fragment, useDeferredValue, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertCircle,
  ChevronDown,
  Download,
  Loader2,
  Pencil,
  Search,
  UserRoundSearch,
  UsersRound,
  X,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatPhone, initials } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useCustomerSearch, useExportMarketingCustomers } from './customers.api';
import { CustomerDetail, LocationSummary } from './customer-detail';
import { CustomerStats } from './customer-stats';
import { EditCustomerSheet } from './edit-customer-sheet';
import type { Customer } from './types';

/**
 * Customer lookup — the support desk's most-used screen.
 *
 * The endpoint has no "list all" mode, so the page is a search first and a
 * table second. Three things the original got wrong:
 *
 *  - It ran the same request from an `onSearch` handler *and* an effect keyed
 *    on page/rows/reload, so paging re-fired the search and raced the handler.
 *  - `count` was set from `response.data.data.count` in one path and
 *    `results.length` in the other, so the same search reported two totals.
 *  - The search term lived only in component state, so a result could not be
 *    linked to or survive a refresh. It is in the URL now.
 */

function useSearchTerm() {
  const [params, setParams] = useSearchParams();
  const term = params.get('q') ?? '';

  const setTerm = (next: string) => {
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        if (next.trim()) updated.set('q', next);
        else updated.delete('q');
        return updated;
      },
      { replace: true }
    );
  };

  return [term, setTerm] as const;
}

function CustomerName({ customer }: { customer: Customer }) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <Avatar className="size-8 shrink-0">
        {customer.avatar && <AvatarImage src={customer.avatar} alt="" />}
        <AvatarFallback>
          {initials(customer.firstName, customer.lastName)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <p className="truncate font-medium">
          {[customer.firstName, customer.lastName].filter(Boolean).join(' ') ||
            '—'}
        </p>
        <LocationSummary customer={customer} />
      </div>
    </div>
  );
}

function ExpandToggle({ open }: { open: boolean }) {
  return (
    <ChevronDown
      className={cn(
        'text-muted-foreground size-4 shrink-0 transition-transform duration-200',
        open && 'rotate-180'
      )}
    />
  );
}

export function CustomersPage() {
  const { t } = useTranslation();
  const [term, setTerm] = useSearchTerm();
  const [draft, setDraft] = useState(term);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editing, setEditing] = useState<Customer | null>(null);

  // Keeps the input responsive while a slow search is in flight.
  const deferredTerm = useDeferredValue(term);
  const search = useCustomerSearch(deferredTerm);
  const exportCsv = useExportMarketingCustomers();

  // A pasted link with ?q= must fill the box, but typing must not be clobbered.
  useEffect(() => {
    setDraft((current) => (current === '' && term !== '' ? term : current));
  }, [term]);

  useEffect(() => {
    if (exportCsv.isError) toast.error(t('customers.exportFailed'));
  }, [exportCsv.isError, t]);

  const customers = search.data?.results ?? [];
  const hasTerm = term.trim().length > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('customers.title')}
        description={t('customers.subtitle')}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportCsv.mutate()}
            disabled={exportCsv.isPending}
          >
            {exportCsv.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              <Download />
            )}
            <span className="hidden sm:inline">
              {t('customers.exportMarketing')}
            </span>
            <span className="sm:hidden">{t('customers.exportShort')}</span>
          </Button>
        }
      />

      <CustomerStats />

      <form
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          setTerm(draft);
        }}
        className="flex gap-2"
      >
        <div className="relative flex-1">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('customers.searchPlaceholder')}
            aria-label={t('customers.searchLabel')}
            autoFocus
            // Search is submit-driven, not keystroke-driven: the endpoint is a
            // POST over the whole user table and a support agent types a full
            // email or phone number, not a prefix.
            className="h-10 pr-9 pl-9"
          />
          {draft && (
            <button
              type="button"
              onClick={() => {
                setDraft('');
                setTerm('');
              }}
              aria-label={t('customers.clearSearch')}
              className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 flex size-6 -translate-y-1/2 items-center justify-center rounded"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        <Button type="submit" className="h-10" disabled={!draft.trim()}>
          <Search className="sm:hidden" />
          <span className="hidden sm:inline">{t('common.search')}</span>
        </Button>
      </form>

      {/* ---------------------------------------------------------- states */}

      {!hasTerm && (
        <EmptyState
          icon={<UserRoundSearch className="size-7" />}
          title={t('customers.startTitle')}
          body={t('customers.startBody')}
        />
      )}

      {hasTerm && search.isPending && <ResultsSkeleton />}

      {hasTerm && search.isError && (
        <EmptyState
          tone="destructive"
          icon={<AlertCircle className="size-7" />}
          title={t('customers.errorTitle')}
          body={
            search.error instanceof Error
              ? search.error.message
              : t('customers.errorBody')
          }
          action={
            <Button variant="outline" size="sm" onClick={() => search.refetch()}>
              {t('common.retry')}
            </Button>
          }
        />
      )}

      {hasTerm && search.isSuccess && customers.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={search.isFetching} />
          <div className={cn(search.isFetching && 'opacity-60 transition-opacity')}>
            <EmptyState
              icon={<UsersRound className="size-7" />}
              title={t('customers.noResultsTitle')}
              body={t('customers.noResultsBody', { term })}
            />
          </div>
        </div>
      )}

      {hasTerm && search.isSuccess && customers.length > 0 && (
        <div className="space-y-3">
          <p className="text-muted-foreground text-sm" aria-live="polite">
            {t('customers.resultCount', { count: customers.length })}
          </p>

          {/* Desktop: a table. */}
          <div className="hidden rounded-xl border md:block">
            <Table busy={search.isFetching}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-8" />
                  <TableHead>{t('customers.name')}</TableHead>
                  <TableHead>{t('customers.email')}</TableHead>
                  <TableHead>{t('customers.phone')}</TableHead>
                  <TableHead className="text-right">
                    {t('customers.orders')}
                  </TableHead>
                  <TableHead>{t('customers.status')}</TableHead>
                  <TableHead className="w-16 text-right">
                    {t('customers.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((customer) => {
                  const open = expanded === customer.id;
                  return (
                    <Fragment key={customer.id}>
                      <TableRow
                        onClick={() =>
                          setExpanded(open ? null : customer.id)
                        }
                        aria-expanded={open}
                        className={cn(
                          'group cursor-pointer',
                          open && 'bg-muted/40 border-b-0'
                        )}
                      >
                        <TableCell>
                          <ExpandToggle open={open} />
                        </TableCell>
                        <TableCell>
                          <CustomerName customer={customer} />
                        </TableCell>
                        <TableCell>
                          {customer.email ? (
                            <span className="flex items-center gap-1">
                              <span className="max-w-[16rem] truncate">
                                {customer.email}
                              </span>
                              <CopyButton value={customer.email} />
                            </span>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {formatPhone(customer.phoneNumber)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {customer.orderCount ?? 0}
                        </TableCell>
                        <TableCell>
                          {customer.deletedAt ? (
                            <Badge variant="destructive">
                              {t('customers.deleted')}
                            </Badge>
                          ) : (
                            <Badge variant="success">
                              {t('customers.active')}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t('customers.editUser')}
                            onClick={(event) => {
                              // The row toggles the detail panel; the button
                              // must not do both.
                              event.stopPropagation();
                              setEditing(customer);
                            }}
                          >
                            <Pencil className="size-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                      {open && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={7} className="bg-muted/40 p-4">
                            <CustomerDetail customer={customer} />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Phone: cards. A six-column table in a horizontal scroller is not a
              mobile layout — the original made you scroll sideways to reach the
              order count. */}
          <div className="relative space-y-2 md:hidden">
            <BusyOverlay show={search.isFetching} />
            {customers.map((customer) => {
              const open = expanded === customer.id;
              return (
                <div
                  key={customer.id}
                  className={cn(
                    'bg-card overflow-hidden rounded-xl border',
                    search.isFetching && 'opacity-60 transition-opacity'
                  )}
                >
                  <button
                    type="button"
                    onClick={() => setExpanded(open ? null : customer.id)}
                    aria-expanded={open}
                    className="flex w-full items-center gap-3 p-3 text-left"
                  >
                    <div className="min-w-0 flex-1 space-y-1">
                      <CustomerName customer={customer} />
                      <p className="text-muted-foreground truncate text-xs">
                        {customer.email || formatPhone(customer.phoneNumber)}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <Badge
                        variant={customer.deletedAt ? 'destructive' : 'success'}
                      >
                        {customer.deletedAt
                          ? t('customers.deleted')
                          : t('customers.active')}
                      </Badge>
                      <span className="text-muted-foreground text-xs tabular-nums">
                        {t('customers.ordersCount', {
                          count: customer.orderCount ?? 0,
                        })}
                      </span>
                    </div>
                    <ExpandToggle open={open} />
                  </button>
                  {open && (
                    <div className="bg-muted/40 space-y-3 border-t p-3">
                      <CustomerDetail customer={customer} />
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => setEditing(customer)}
                      >
                        <Pencil />
                        {t('customers.editUser')}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <EditCustomerSheet
        customer={editing}
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
      />
    </div>
  );
}

// ------------------------------------------------------------------ helpers

function EmptyState({
  icon,
  title,
  body,
  action,
  tone = 'muted',
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  action?: React.ReactNode;
  tone?: 'muted' | 'destructive';
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed px-6 py-14 text-center">
      <span
        className={cn(
          'flex size-14 items-center justify-center rounded-full',
          tone === 'destructive'
            ? 'bg-destructive/10 text-destructive'
            : 'bg-muted text-muted-foreground'
        )}
      >
        {icon}
      </span>
      <div className="space-y-1">
        <p className="font-medium">{title}</p>
        <p className="text-muted-foreground max-w-sm text-sm text-pretty">
          {body}
        </p>
      </div>
      {action}
    </div>
  );
}

function ResultsSkeleton() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-5 w-32" />
      <div className="divide-y rounded-xl border">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="flex items-center gap-3 p-3">
            <Skeleton className="size-8 shrink-0 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-56" />
            </div>
            <Skeleton className="hidden h-5 w-16 sm:block" />
          </div>
        ))}
      </div>
    </div>
  );
}
